import { DataTable, useColumns } from "@/components/data-table";
import { ListPage } from "@/components/list-page";
import { useFilters } from "@/components/filters";
import { DataFetcher } from "@/components/display";
import { ConfirmDialog, Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { useGetDocumentosElectronicosQuery } from "@/features/facturacion-electronica/api/get-documentos-electronicos";
import { GetDEQuery } from "@/features/facturacion-electronica/api/get-de";
import { downloadKude, getKudeTicketDatos, useEliminarDEReservadoMutation, useReservarDEMutation } from "@/features/facturacion-electronica/api/de-actions";
import DEView from "@/features/facturacion-electronica/components/de-view";
import KudeTicketView from "@/features/facturacion-electronica/components/kude-ticket-print/kude-ticket-view";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import { formatDate, formatString, formatNumber } from "@/shared/formats";
import { DEEstadoEnum, DEEstadoInicialEnum, DEEstadoProcesoEnum, DETipoDocumentoEnum } from "@/shared/types/documento-electronico";
import { Permiso } from "@/shared/types/permiso";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { usePrinter } from "@/components/print";
import { Chip, Tooltip, Button, Stack } from "@mui/material";
import { Print, Receipt, CreditCard, Description, BookmarkAdd, Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router";
import { useRef, useState } from "react";
import { notify } from "@/shared/notification";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}


const getEstadoLabel = (estado?: number) => {
    switch (estado) {
        case DEEstadoEnum.Inactivo: return "Inactivo";
        case DEEstadoEnum.Activo: return "Activo";
        case DEEstadoEnum.Cancelado: return "Cancelado";
        case DEEstadoEnum.Inutilizado: return "Inutilizado";
        case DEEstadoEnum.Devuelto: return "Devuelto";
        case DEEstadoEnum.Reservado: return "Reservado";
        default: return "-";
    }
};

const getEstadoColor = (estado?: number) => {
    switch (estado) {
        case DEEstadoEnum.Inactivo: return "default";
        case DEEstadoEnum.Activo: return "success";
        case DEEstadoEnum.Cancelado: return "warning";
        case DEEstadoEnum.Inutilizado: return "warning";
        case DEEstadoEnum.Devuelto: return "info";
        case DEEstadoEnum.Reservado: return "secondary";
        default: return "default";
    }
};

const getEstadoInicialLabel = (estado?: number) => {
    switch (estado) {
        case DEEstadoInicialEnum.NoEnviado: return "No Enviado";
        case DEEstadoInicialEnum.Procesado: return "Procesado";
        case DEEstadoInicialEnum.EnSifen: return "En SIFEN";
        case DEEstadoInicialEnum.ErrorEnvio: return "Error Envío";
        default: return "-";
    }
};

const getEstadoProcesoLabel = (estado?: number) => {
    switch (estado) {
        case DEEstadoProcesoEnum.Aprobado: return "Aprobado";
        case DEEstadoProcesoEnum.AprobadoConObservacion: return "Aprobado con Observación";
        case DEEstadoProcesoEnum.Rechazado: return "Rechazado";
        case DEEstadoProcesoEnum.EnProceso: return "En Proceso";
        default: return "-";
    }
};

/*const getEstadoProcesoColor = (estado?: number) => {
    switch (estado) {
        case DEEstadoProcesoEnum.Aprobado: return "success";
        case DEEstadoProcesoEnum.AprobadoConObservacion: return "warning";
        case DEEstadoProcesoEnum.Rechazado: return "error";
        case DEEstadoProcesoEnum.EnProceso: return "info";
        default: return "default";
    }
};*/

const getTipoDocumentoLabel = (tipo?: number) => {
    switch (tipo) {
        case 1: return "Factura Electrónica";
        case 4: return "Autofactura";
        case 5: return "Nota de Crédito";
        case 7: return "Nota de Remisión";
        default: return "-";
    }
};

export default function DocumentosElectronicosPage() {
    const openModal = useOpenModal();
    const navigate = useNavigate();
    const hasPermisos = useHasPermisos(Permiso.WriteDocumentosElectronicos);
    const hasReservarPermiso = useHasPermisos(Permiso.ReservarDocumentosElectronicos);
    const emisorConfig = useEmisorConfig();
    const { modeTicket, printPaperWidth } = useConfigurationContext();
    const printer = usePrinter();
    const [printingId, setPrintingId] = useState<string | null>(null);
    const reservarDE = useReservarDEMutation({});
    const eliminarReservado = useEliminarDEReservadoMutation({});
    const pendingDeleteId = useRef<string | null>(null);

    const handlePrintKude = async (deId: string, numeroDocumento?: string | number | null) => {
        try {
            setPrintingId(deId);
            if (modeTicket) {
                const response = await getKudeTicketDatos(deId);
                await printer.print({
                    documentTitle: `KUDE N°${numeroDocumento}`,
                    printComponent: <KudeTicketView datos={response.data} />,
                    pageStyle: `@page { size: ${printPaperWidth} auto; margin: 0; }`
                });
            } else {
                const blob = await downloadKude(deId);
                const base64 = await blobToBase64(blob);
                openPdfFromBase64(base64);
            }
        } catch (error) {
            notify("Error al imprimir el KuDE", { variant: "error" });
        } finally {
            setPrintingId(null);
        }
    };

    // Todos los filtros definidos aquí
    const filterData = useFilters({
        filters: [
            { type: "string", label: "CDC", name: "cdc" },
            {
                type: "select", label: "Tipo Documento", name: "tipoDocumento", options: [
                    { value: DETipoDocumentoEnum.FacturaElectronica, label: "Factura Electrónica" },
                    { value: DETipoDocumentoEnum.NotaCredito, label: "Nota de Crédito" },
                    { value: DETipoDocumentoEnum.Autofactura, label: "Autofactura" },
                    { value: DETipoDocumentoEnum.NotaRemision, label: "Nota de Remisión" }
                ]
            },
            { type: "string", label: "Establecimiento", name: "establecimiento", row: "est-pe" },
            { type: "string", label: "Punto Expedición", name: "puntoExpedicion", row: "est-pe" },
            {
                type: "select", label: "Estado Inicial", name: "estadoInicial", options: [
                    { value: DEEstadoInicialEnum.NoEnviado, label: "No Enviado" },
                    { value: DEEstadoInicialEnum.Procesado, label: "Procesado" },
                    { value: DEEstadoInicialEnum.EnSifen, label: "En SIFEN" },
                    { value: DEEstadoInicialEnum.ErrorEnvio, label: "Error Envío" }
                ]
            },
            {
                type: "select", label: "Estado Proceso", name: "estadoProceso", options: [
                    { value: DEEstadoProcesoEnum.Aprobado, label: "Aprobado" },
                    { value: DEEstadoProcesoEnum.AprobadoConObservacion, label: "Aprobado con Observación" },
                    { value: DEEstadoProcesoEnum.Rechazado, label: "Rechazado" },
                    { value: DEEstadoProcesoEnum.EnProceso, label: "En Proceso" }
                ]
            },
            {
                type: "select", label: "Estado", name: "estado", options: [
                    { value: DEEstadoEnum.Inactivo, label: "Inactivo" },
                    { value: DEEstadoEnum.Activo, label: "Activo" },
                    { value: DEEstadoEnum.Cancelado, label: "Cancelado" },
                    { value: DEEstadoEnum.Inutilizado, label: "Inutilizado" },
                    ...(hasReservarPermiso ? [{ value: DEEstadoEnum.Reservado, label: "Reservado" }] : [])
                ]
            },
            { type: "dateRange", label: "Fecha Emisión", fromName: "fechaEmisionDesde", toName: "fechaEmisionHasta" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetDocumentosElectronicosQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            {
                key: "cdc",
                header: "CDC",
                render: row => row.cdc ? (
                    <Tooltip title={row.cdc} arrow>
                        <span style={{ cursor: "help", whiteSpace: "nowrap" }}>…{row.cdc.slice(-10)}</span>
                    </Tooltip>
                ) : "-",
                align: "left",
                width: "110px"
            },
            { key: "tipoDocumento", header: "Tipo", render: row => getTipoDocumentoLabel(row.tipoDocumento), align: "left", width: "auto" },
            { key: "numeroDocumento", header: "N°", render: row => row.numeroDocumento || "-", align: "center", width: "80px" },
            { key: "establecimiento", header: "Est", render: row => row.establecimiento ? row.establecimiento.padStart(3, "0") : "---", align: "center", width: "60px" },
            { key: "puntoExpedicion", header: "PE", render: row => row.puntoExpedicion ? row.puntoExpedicion.padStart(3, "0") : "---", align: "center", width: "60px" },
            {
                key: "numeroVenta", header: "N° Venta", render: row => row.numeroVenta ?? "---", show: false, width: "60px"
            },
            { key: "fechaEmisionDE", header: "Emisión", render: row => formatDate(row.fechaEmisionDE), align: "center", width: "100px" },
            {
                key: "nombreReceptor",
                header: "Cliente",
                render: row => row.nombreReceptor ? (
                    <Tooltip title={row.nombreReceptor} arrow>
                        <span style={{ cursor: "help", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}>
                            {row.nombreReceptor}
                        </span>
                    </Tooltip>
                ) : "---",
                align: "left",
                width: "200px"
            },
            { key: "total", header: "Total", render: row => row.tipoDocumento === DETipoDocumentoEnum.NotaRemision ? "-" : formatNumber(row.total, row.cMoneOpe == "PYG" ? 0 : 2), align: "right", width: "120px" },
            { key: "moneda", header: "Moneda", render: row => row.cMoneOpe ?? "-", align: "center", width: "80px", show: false },
            { key: "estadoInicial", header: "Estado Inicial", render: row => getEstadoInicialLabel(row.estadoInicial), align: "center", show: false },
            {
                key: "estadoProceso",
                header: "Estado Proceso",
                render: row => getEstadoProcesoLabel(row.estadoProceso),
                align: "center",
                width: "140px"
            },
            // Hidden columns for additional info
            { key: "serieDocumento", header: "Serie", render: row => formatString(row.serieDocumento), align: "center", show: false },
            { key: "fechaProceso", header: "Fecha Procesado", render: row => formatDate(row.fechaProceso), align: "center", show: false },
            { key: "descripcionProceso", header: "Descripción Procesado", render: row => formatString(row.descripcionProceso), align: "left", show: false },
            {
                key: "estado",
                header: "Estado",
                render: row => (
                    <Chip
                        label={getEstadoLabel(row.estado)}
                        color={getEstadoColor(row.estado) as any}
                        size="small"
                    />
                ),
                align: "center",
            },
            { key: "timbrado", header: "Timbrado", render: row => row.timbrado, show: false },
            { key: "observaciones", header: "Observaciones", render: row => formatString(row.observaciones), show: false },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "documentosElectronicos"
    });

    return (
        <ListPage wide>
            <ListPage.Header>
                <ListPage.Header.Actions title="Documentos Electrónicos">
                    {hasReservarPermiso && (
                        <Button
                            variant="outlined"
                            startIcon={<BookmarkAdd />}
                            onClick={() => openModal("reservar")}
                            size="small"
                        >
                            Reservar Numeración
                        </Button>
                    )}
                    {hasPermisos && (
                        <Stack direction="row" gap={1}>
                            <Button
                                variant="contained"
                                startIcon={<Receipt />}
                                onClick={() => navigate("/nueva-factura-electronica")}
                                size="small"
                            >
                                Nueva Factura Electrónica
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CreditCard />}
                                onClick={() => navigate("/nueva-nota-credito")}
                                size="small"
                            >
                                Nueva Nota de Crédito
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Description />}
                                onClick={() => navigate("/nueva-autofactura")}
                                size="small"
                            >
                                Nueva Autofactura
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Description />}
                                onClick={() => navigate("/nueva-nota-remision")}
                                size="small"
                            >
                                Nueva Nota de Remisión
                            </Button>
                        </Stack>
                    )}
                    <ListPage.Header.Actions.FilterButton />
                    <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                </ListPage.Header.Actions>
                <ListPage.Header.Filters filterData={filterData} dialog />
            </ListPage.Header>

            <DataTable flex={1}>
                <DataTable.Content
                    rows={fetchData.data?.result}
                    columns={[
                        ...columnData.columns,
                        {
                            header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                                <DataTable.EntryOption.ViewButton onClick={() => openModal("view", row.id)} />
                                {row.estado !== DEEstadoEnum.Reservado && (
                                    <DataTable.EntryOption.Button
                                        Icon={Print}
                                        title="Imprimir KuDE"
                                        onClick={() => handlePrintKude(row.id, row.numeroDocumento)}
                                        disabled={!row.cdc || printingId === row.id}
                                    />
                                )}
                                {row.estado === DEEstadoEnum.Reservado && hasReservarPermiso && (
                                    <>
                                        {row.tipoDocumento === DETipoDocumentoEnum.FacturaElectronica && (
                                            <DataTable.EntryOption.Button
                                                Icon={Edit}
                                                title="Completar Reserva"
                                                onClick={() => navigate(`/nueva-factura-electronica?reservadoId=${row.id}`)}
                                            />
                                        )}
                                        <DataTable.EntryOption.Button
                                            Icon={Delete}
                                            title="Eliminar Reserva"
                                            onClick={() => {
                                                pendingDeleteId.current = row.id;
                                                openModal("confirmarEliminarReserva");
                                            }}
                                        />
                                    </>
                                )}
                            </DataTable.EntryOption>
                        }
                    ]}
                    fit="flex"
                    loading={fetchData.isFetching}
                    error={fetchData.error}
                />
                <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
            </DataTable>

            <Dialog code="view" size="lg">
                <ModalHeader>Detalles del Documento Electrónico</ModalHeader>
                <ModalValueExtractor>
                    {deId => <GetDEQuery id={deId}>
                        {query => <DataFetcher query={query} loadMode="loading">
                            {de => <DEView de={de} />}
                        </DataFetcher>}
                    </GetDEQuery>}
                </ModalValueExtractor>
            </Dialog>

            <ConfirmDialog code="reservar">
                <ConfirmDialog.Content
                    title="Reservar numeración FE"
                    description={
                        emisorConfig.isConfigValid
                            ? `Se reservará la siguiente numeración de Factura Electrónica disponible para el establecimiento ${emisorConfig.config?.establecimiento} y punto de expedición ${emisorConfig.config?.puntoExpedicion} con la fecha y hora actual.`
                            : "No hay configuración de emisor. Configure el establecimiento y punto de expedición primero."
                    }
                    confirmText="Reservar"
                    onConfirm={() => {
                        if (emisorConfig.isConfigValid) {
                            reservarDE.mutate({
                                tipoDocumento: DETipoDocumentoEnum.FacturaElectronica,
                                establecimiento: emisorConfig.config!.establecimiento,
                                puntoExpedicion: emisorConfig.config!.puntoExpedicion,
                            });
                        }
                    }}
                />
            </ConfirmDialog>

            <ConfirmDialog code="confirmarEliminarReserva">
                <ConfirmDialog.Content
                    title="Eliminar numeración reservada"
                    description="¿Está seguro que desea eliminar esta numeración reservada? Esta acción no se puede deshacer."
                    confirmText="Eliminar"
                    onConfirm={() => {
                        if (pendingDeleteId.current) {
                            eliminarReservado.mutate(pendingDeleteId.current);
                            pendingDeleteId.current = null;
                        }
                    }}
                />
            </ConfirmDialog>
            <printer.Printer />
        </ListPage>
    );
}

